
import React from "react";
import { Card } from "@/components/ui/card";
import { CalendarClock } from "lucide-react";
import { DatePicker } from "./DatePicker";
import { PostStatus } from "@/hooks/useBlogPosts";

interface PublicationOptionsProps {
  status: PostStatus;
  scheduledFor: Date | null;
  onStatusChange: (status: PostStatus) => void;
  onDateChange: (date: Date | null) => void;
  disabled?: boolean;
}

export function PublicationOptions({
  status,
  scheduledFor,
  onStatusChange,
  onDateChange,
  disabled = false,
}: PublicationOptionsProps) {
  return (
    <Card className="p-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center">
          <CalendarClock className="h-5 w-5 mr-2 text-primary" />
          <h3 className="text-lg font-medium">Publication Options</h3>
        </div>
      </div>
      
      <div className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
          <Card 
            className={`p-4 cursor-pointer border-2 transition-all ${status === 'draft' ? 'border-primary bg-primary/5' : 'border-transparent hover:border-slate-200'}`} 
            onClick={() => !disabled && onStatusChange('draft')}
            tabIndex={disabled ? -1 : 0}
          >
            <h4 className="font-medium mb-1">Draft</h4>
            <p className="text-sm text-slate-500">Save as a draft - not visible to visitors</p>
          </Card>
          
          <Card 
            className={`p-4 cursor-pointer border-2 transition-all ${status === 'published' ? 'border-primary bg-primary/5' : 'border-transparent hover:border-slate-200'}`}
            onClick={() => !disabled && onStatusChange('published')}
            tabIndex={disabled ? -1 : 0}
          >
            <h4 className="font-medium mb-1">Publish Now</h4>
            <p className="text-sm text-slate-500">Publish immediately - visible to visitors</p>
          </Card>
          
          <Card 
            className={`p-4 cursor-pointer border-2 transition-all ${status === 'scheduled' ? 'border-primary bg-primary/5' : 'border-transparent hover:border-slate-200'}`}
            onClick={() => !disabled && onStatusChange('scheduled')}
            tabIndex={disabled ? -1 : 0}
          >
            <h4 className="font-medium mb-1">Schedule</h4>
            <p className="text-sm text-slate-500">Schedule for future publication</p>
          </Card>
        </div>
        
        {status === 'scheduled' && (
          <div className="bg-slate-50 p-4 rounded-lg">
            <h4 className="font-medium mb-2">Schedule Publication</h4>
            <p className="text-sm text-slate-500 mb-4">
              Set a date and time when this post should be published automatically.
            </p>
            <DatePicker 
              date={scheduledFor} 
              onDateChange={onDateChange}
              disabled={disabled}
            />
          </div>
        )}
      </div>
    </Card>
  );
}
